import type {
  IFetchObservationTimelineEntriesParams,
  IObservationTimelineEntity,
} from '@/api/resources/observationTimeline.api';

const OBSERVATION_TIMELINE_ALL_KEY = ['observationTimelineEntries'] as const;

enum EObservationTimelineQueryScope {
  Entry = 'entry',
  List = 'list',
  Stats = 'stats',
}

export interface IObservationTimelineListKeyParams
  extends Omit<IFetchObservationTimelineEntriesParams, 'schoolClassId' | 'currentPage'> {
  schoolClassId?: number | null;
}

export type IObservationTimelineStatsKeyParams = Omit<IObservationTimelineListKeyParams, 'pageSize'>;

const scopedKey = <T extends EObservationTimelineQueryScope>(
  scope: T,
  schoolClassId?: number | null,
):
  | readonly [...typeof OBSERVATION_TIMELINE_ALL_KEY, T]
  | readonly [...typeof OBSERVATION_TIMELINE_ALL_KEY, T, number] =>
  schoolClassId == null
    ? ([...OBSERVATION_TIMELINE_ALL_KEY, scope] as const)
    : ([...OBSERVATION_TIMELINE_ALL_KEY, scope, schoolClassId] as const);

const filterKey = (params: IObservationTimelineStatsKeyParams): IObservationTimelineStatsKeyParams => ({
  schoolClassId: params.schoolClassId ?? null,
  entryType: params.entryType ?? null,
  schoolSubjectId: params.schoolSubjectId ?? null,
  searchText: params.searchText ?? '',
  performanceAssessmentId: params.performanceAssessmentId ?? null,
  filterIncomplete: params.filterIncomplete ?? false,
  filterActiveSupportMeasures: params.filterActiveSupportMeasures ?? false,
  includeSupportMeasures: params.includeSupportMeasures ?? true,
  studentId: params.studentId ?? null,
  competenceId: params.competenceId ?? null,
  competenceDomainId: params.competenceDomainId ?? null,
  fromDate: params.fromDate,
  toDate: params.toDate,
});

export const observationTimelineKeys = {
  entries: (schoolClassId?: number | null) => scopedKey(EObservationTimelineQueryScope.Entry, schoolClassId),

  entity: (schoolClassId: number, entity: IObservationTimelineEntity) =>
    [...observationTimelineKeys.entries(schoolClassId), entity.type, entity.id] as const,

  entry: (schoolClassId: number, studentId: number | null, entity: IObservationTimelineEntity) =>
    [...observationTimelineKeys.entity(schoolClassId, entity), studentId] as const,

  lists: (schoolClassId?: number | null) => scopedKey(EObservationTimelineQueryScope.List, schoolClassId),

  list: (params: IObservationTimelineListKeyParams) =>
    [
      ...OBSERVATION_TIMELINE_ALL_KEY,
      EObservationTimelineQueryScope.List,
      params.schoolClassId ?? null,
      { ...filterKey(params), pageSize: params.pageSize },
    ] as const,

  stats: (schoolClassId?: number | null) => scopedKey(EObservationTimelineQueryScope.Stats, schoolClassId),

  stat: (params: IObservationTimelineStatsKeyParams) =>
    [
      ...OBSERVATION_TIMELINE_ALL_KEY,
      EObservationTimelineQueryScope.Stats,
      params.schoolClassId ?? null,
      filterKey(params),
    ] as const,
};

export type TObservationTimelineListKey = ReturnType<typeof observationTimelineKeys.list>;
